import 'react-datepicker/dist/react-datepicker.css';

import * as Yup from 'yup';

import { Button, Form, Spinner } from 'react-bootstrap';
import { Field, Formik } from 'formik';
import React, { useContext, useEffect, useState } from 'react';
import { contentfulPropTypes, onboardingPropTypes } from '../../propTypes';
import { flowSteps, recordFlowProgress } from '../../utils/recordFlowProgress';

import { ConsentContext } from '../../context/ConsentContext';
import ErrorLabel from '../../ui/ErrorLabel';
import { RadioWrapper } from '../../ui/Styled';
import ReactMarkdown from 'react-markdown';
import cx from 'classnames';
import getDateOptions from '../../utils/getDateOptions';
import moment from 'moment';
import { useNavigate } from 'react-router';
import withContentfulTag from '../../utils/withContentfulTag';

const moveYears = getDateOptions(2, 'years', 'YYYY');
const moveMonths = getDateOptions(12, 'months', 'MMMM');

const MoveDateContainer = ({ contentful, loading, fetched }) => {
  const { consentFlow, setMoveDate } = useContext(ConsentContext);
  const navigate = useNavigate();
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    const asyncRecordFlowProgress = async () => {
      await recordFlowProgress(flowSteps.THIRTEEN_MONTHS_1);
    };
    asyncRecordFlowProgress();
  }, []);

  const onSubmitMoveDate = values => {
    if (values.moveRadio === 'more-than') {
      setMoveDate(undefined);
    } else {
      setMoveDate(
        moment(`${values.moveDateMonth} ${values.moveDateYear}`, 'MMMM YYYY'),
      );
    }
    navigate(`/${consentFlow}/mpan`);
  };

  const schema = Yup.object().shape({
    moveRadio: Yup.mixed().oneOf(['more-than', 'less-than']).required(),
    moveDateMonth: Yup.mixed().oneOf(moveMonths, 'Please select the month'),
    moveDateYear: Yup.mixed().oneOf(moveYears, 'Please select the year'),
  });

  const shouldDisableSubmit = (values, touched) => {
    if (!touched) {
      return true;
    }
    if (values.moveRadio === 'more-than') {
      return false;
    }
    if (
      values.moveRadio === 'less-than' &&
      moveYears.includes(values.moveDateYear) &&
      moveMonths.includes(values.moveDateMonth)
    ) {
      if (
        moment(
          `${values.moveDateMonth} ${values.moveDateYear}`,
          'MMMM YYYY',
        ).isAfter()
      ) {
        return true;
      }
      return false;
    }
    if (
      moment(
        `${values.moveDateMonth} ${values.moveDateYear}`,
        'MMMM YYYY',
      ).isBefore()
    ) {
      return false;
    }
    return true;
  };

  return (
    !loading &&
    fetched && (
      <>
        <p>
          Step <strong>1</strong> of <strong>3</strong>
        </p>
        <h1 className="mb-3">{contentful?.title}</h1>
        <div>
          <ReactMarkdown>{contentful?.body}</ReactMarkdown>
        </div>
        <Formik
          onSubmit={onSubmitMoveDate}
          initialValues={{
            moveRadio: '',
            moveDateYear: '',
            moveDateMonth: '',
          }}
          validationSchema={schema}>
          {({ handleSubmit, isSubmitting, values }) => (
            <Form onSubmit={handleSubmit}>
              <Field name="moveRadio">
                {({ field, meta }) => (
                  <RadioWrapper
                    className={cx({ selected: field.value === 'more-than' })}>
                    <Form.Check
                      type="radio"
                      name={field.name}
                      id="more-less-than"
                      value="more-than"
                      label={contentful?.primaryButtonText}
                      onChange={e => {
                        setTouched(true);
                        field.onChange(e);
                      }}
                      onBlur={field.onBlur}
                      className={cx({ error: !!meta.error })}
                    />
                  </RadioWrapper>
                )}
              </Field>
              <Field name="moveRadio">
                {({ field, meta }) => (
                  <RadioWrapper
                    className={cx({ selected: field.value === 'less-than' })}>
                    <Form.Check
                      type="radio"
                      name={field.name}
                      id="radio-less-than"
                      value="less-than"
                      label={contentful?.secondaryButtonText}
                      onChange={async e => {
                        setTouched(true);
                        await recordFlowProgress(flowSteps.THIRTEEN_MONTHS_2);
                        field.onChange(e);
                      }}
                      onBlur={field.onBlur}
                      className={cx({ error: !!meta.error })}
                    />
                    {field.value === 'less-than' && (
                      <div className="p-2 pl-5">
                        <div>
                          <p>
                            <strong>{contentful?.subtitle}</strong>
                          </p>
                        </div>
                        <div className="d-flex flex-row">
                          <span className="flex-fill">
                            <Field name="moveDateMonth">
                              {({ field, meta }) => (
                                <>
                                  <Form.Control
                                    name={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    isValid={!meta.error && meta.touched}
                                    className={cx({
                                      error: !!meta.error && !!meta.touched,
                                    })}
                                    as="select"
                                    placeholder="Month">
                                    <option value="">Month</option>
                                    {moveMonths.map((month, index) => (
                                      <option
                                        key={`month-${month}-${index}`}
                                        value={month}>
                                        {month}
                                      </option>
                                    ))}
                                  </Form.Control>
                                  {meta.error && meta.touched && (
                                    <ErrorLabel label={meta.error} />
                                  )}
                                </>
                              )}
                            </Field>
                          </span>
                          <span className="mt-2 px-2">/</span>
                          <span className="flex-fill">
                            <Field name="moveDateYear">
                              {({ field, meta }) => (
                                <>
                                  <Form.Control
                                    name={field.name}
                                    value={field.value}
                                    onChange={field.onChange}
                                    onBlur={field.onBlur}
                                    isValid={!meta.error && meta.touched}
                                    className={cx({
                                      error: !!meta.error && !!meta.touched,
                                    })}
                                    as="select"
                                    placeholder="Year">
                                    <option value="">Year</option>
                                    {moveYears.map((year, index) => (
                                      <option
                                        key={`year-${year}-${index}`}
                                        value={year}>
                                        {year}
                                      </option>
                                    ))}
                                  </Form.Control>
                                  {meta.error && meta.touched && (
                                    <ErrorLabel label={meta.error} />
                                  )}
                                </>
                              )}
                            </Field>
                          </span>
                        </div>
                      </div>
                    )}
                  </RadioWrapper>
                )}
              </Field>
              <Button
                className="w-100"
                type="submit"
                variant="primary"
                disabled={shouldDisableSubmit(values, touched)}>
                {!isSubmitting ? (
                  'Continue'
                ) : (
                  <Spinner animation="border" size="sm" />
                )}
              </Button>
            </Form>
          )}
        </Formik>
      </>
    )
  );
};

MoveDateContainer.propTypes = {
  ...contentfulPropTypes,
  ...onboardingPropTypes,
};

export default withContentfulTag(
  MoveDateContainer,
  'web_app_onboarding_move_date',
);
